/* eslint-disable no-restricted-imports */
import axios from 'axios';
import { normalize } from 'normalizr';
import { toCollectionsModel } from '../../mappers/collections/toCollectionsModel';
import { toPostModel } from '../../mappers/posts/toPostModel';
import { toPostPersistenceDto } from '../../mappers/posts/toPostPersistenceDto';
import { toPostStatisticModel } from '../../mappers/posts/toPostStatisticModel';
import { PostSubmitErrorsMap } from '../../routing/post/constants';
import { contentActionsService } from '../../utils/ContentActionsService/ContentActionsService';
import { canEditBusiness } from '../../utils/accessService';
import { buildUrlWithParams } from '../../utils/buildUrlWithParams';
import { getFileExtension } from '../../utils/getFileExtension';
import { share } from '../../utils/share';
import { collectionsSchema } from '../collections/schema';
import { CollectionModel, CollectionsDataModel } from '../collections/types';
import * as mediaService from '../media/service';
import { MediaModel } from '../media/types';
import { UserModel } from '../user/types';
import { PostSource, PostStatus } from './constants';
import { postSchema } from './schema';
import {
    DeclinedReason,
    type PostDataModel,
    type PostDto,
    type PostModel,
    type PostPersistenceModel,
    type ReportReasons,
} from './types';
import type { EventSource } from '../../utils/analyticsService';
import type { BusinessModel } from '../business/types';
import type { CategoryDataModel } from '../categories/types';

class PostMediaUploadError extends Error {
    constructor(message: string = 'Не удалось загрузить медиа') {
        super(message);
        this.name = 'FileUploadError';
    }
}

function getPostPersistErrorFromErrorsArray(data: unknown): string | undefined {
    if (!data || typeof data !== 'object') {
        return undefined;
    }
    const errors = (data as { errors?: unknown }).errors;
    if (!Array.isArray(errors) || errors.length === 0) {
        return undefined;
    }
    const parts: string[] = [];
    for (const item of errors) {
        if (!item || typeof item !== 'object') {
            continue;
        }
        const e = item as Record<string, unknown>;
        const msg = typeof e.message === 'string' && e.message;
        if (msg) {
            parts.push(msg);
        }
    }

    return parts.length > 0 ? parts.join('; ') : undefined;
}

function getFeedEndpoint(
    businessId?: string | null,
    collectionId?: string | null,
    query?: string | null,
    isDefaultDiscovery?: boolean,
    postIdForDiscovery?: string | null,
    namespace?: string,
) {
    if (businessId) {
        return namespace === 'management'
            ? `/feed/${businessId}/management`
            : `/feed/${businessId}`;
    }
    if (collectionId) {
        return `/collections/feed/${collectionId}`;
    }
    if (query || query === '') {
        return '/feed/search';
    }
    if (isDefaultDiscovery) {
        return '/discovery';
    }
    if (postIdForDiscovery) {
        return `/discovery/${postIdForDiscovery}`;
    }

    return '/feed';
}

export async function loadFeed(
    filters: {
        amount: number;
        categoryId?: string | null;
        businessId?: string | null;
        collectionId?: string | null;
        statuses?: PostStatus[];
        data_sources?: PostSource[];
        query?: string | null;
        push_payload?: string | null;
        first_post_id?: string | null;
        no_log?: string | null;
        next?: string;
        isDefaultDiscovery?: boolean;
        postIdForDiscovery?: string | null;
    },
    namespace?: string,
): Promise<{
    next: string;
    posts: Array<PostDataModel['id']>;
    records: {
        posts: PostDataModel[];
        business: BusinessModel[];
        categories: CategoryDataModel[];
    };
}> {
    const {
        businessId,
        collectionId,
        statuses = [],
        data_sources = [],
        query,
        isDefaultDiscovery = false,
        postIdForDiscovery,
        ...params
    } = filters;

    const feedUrl = getFeedEndpoint(
        businessId,
        collectionId,
        query,
        isDefaultDiscovery,
        postIdForDiscovery,
        namespace,
    );

    const response = await axios.get(feedUrl, {
        params: {
            ...params,
            query: query,
            statuses: statuses.length > 0 ? JSON.stringify(statuses) : undefined,
            data_sources: data_sources.length > 0 ? JSON.stringify(data_sources) : undefined,
        },
    });

    const normalized = normalize(
        {
            ...response.data,
            posts: response.data.posts.map(toPostModel),
        },
        {
            posts: [postSchema],
        },
    );

    return {
        next: normalized.result.next,
        posts: normalized.result.posts,
        records: {
            posts: Object.values(normalized.entities.posts ?? {}),
            business: Object.values(normalized.entities.business ?? {}),
            categories: Object.values(normalized.entities.categories ?? {}),
        },
    };
}

export async function removePosts(identifiers: string[]): Promise<void> {
    return await axios.delete('/posts/', {
        data: {
            post_ids: identifiers,
        },
    });
}

export async function loadPostById(targetId: string): Promise<{
    records: {
        posts: PostDataModel[];
        business: BusinessModel[];
        categories: CategoryDataModel[];
    };
}> {
    const url = buildUrlWithParams(`/posts/${targetId}`);
    const response = await axios.get(url);
    const normalized = normalize({ post: toPostModel(response.data.post) }, { post: postSchema });

    return {
        records: {
            posts: Object.values(normalized.entities.posts ?? {}),
            business: Object.values(normalized.entities.business ?? {}),
            categories: Object.values(normalized.entities.categories ?? {}),
        },
    };
}

export async function persistPost(model: PostPersistenceModel): Promise<{
    data: PostModel;
    error?: string;
}> {
    let response;
    try {
        if (model.id) {
            response = await axios.patch(`/posts/${model.id}`, toPostPersistenceDto(model));
        } else {
            response = await axios.post('/posts', toPostPersistenceDto(model));
        }

        if (!response?.data?.post) {
            return {
                data: {} as PostModel,
                error: 'Ошибка публикации поста',
            };
        }

        return {
            data: toPostModel(response.data.post),
        };
    } catch (err) {
        const error = err as any;
        const data = error?.response?.data;

        const errorMessage =
            getPostPersistErrorFromErrorsArray(data) || data?.message || data?.error;

        const formattedError =
            PostSubmitErrorsMap[errorMessage as keyof typeof PostSubmitErrorsMap] ||
            errorMessage ||
            'Не удалось опубликовать пост';

        return {
            data: {} as PostModel,
            error: formattedError,
        };
    }
}

export async function updatePostReaction({
    reactionState,
    model,
    params,
}: {
    reactionState: PostModel['reactionState'];
    model: PostModel;
    params: {
        postNumber: number;
        eventSource: EventSource;
        collectionId?: string;
    };
}): Promise<Pick<PostDto, 'reaction_state'>> {
    return await axios(`/posts/${model.id}/reaction`, {
        method: 'POST',
        params: {
            post_number: params.postNumber,
            event_source: params.eventSource,
            collection_id: params.collectionId,
        },
        data: {
            reaction_state: reactionState,
            log_context: model.logContext,
        },
    });
}

export async function sharePost(post: PostModel): Promise<ReturnType<typeof share>> {
    return await share({ url: post.shareUrl });
}

export async function reportPost(postId: string, reason: ReportReasons): Promise<void> {
    await axios.put(`/posts/${postId}/report`, {}, { params: { report_reason: reason } });
}

export async function bookmarkPost({
    model,
    collectionId,
}: {
    model: PostModel;
    collectionId?: string;
}): Promise<CollectionModel> {
    const response = model.saved
        ? await axios(`/favourites/${model.id}`, {
              method: 'DELETE',
              ...(collectionId ? { params: { collection_id: collectionId } } : {}),
          })
        : await axios('/favourites', {
              method: 'POST',
              data: {
                  post_id: model.id,
              },
          });

    return response.data.collections.map(toCollectionsModel);
}

export async function bulkUpdateStatus(
    models: PostModel[],
    status: PostStatus,
    user?: UserModel,
): Promise<void> {
    const invalidModels = models.filter((model) => !canChangeStatus(model, status, user));

    if (invalidModels.length > 0) {
        throw new Error('Недопустимый переход статуса');
    }

    await axios.patch(`/posts/status/${status}`, {
        post_ids: models.map((model) => model.id),
    });
}

const STATUS_FINITE_STATE_MACHINE: Record<PostStatus, PostStatus[]> = {
    [PostStatus.DRAFT]: [PostStatus.MODERATION, PostStatus.DELETED],
    [PostStatus.PUBLISHED]: [PostStatus.DELETED],
    [PostStatus.MODERATION]: [PostStatus.DELETED],
    [PostStatus.LIMITED]: [PostStatus.DELETED],
    [PostStatus.DECLINED]: [PostStatus.DELETED],
    [PostStatus.DELETED]: [],
};

export const canChangeStatus = (
    model: PostModel,
    status: PostStatus,
    user?: UserModel,
): boolean => {
    const hasPermission =
        Boolean(user) &&
        (canEditBusiness(model.business?.id, user) || Boolean(model.ownersData?.isOwner));
    const isValidTransition =
        STATUS_FINITE_STATE_MACHINE[model.status ?? PostStatus.PUBLISHED].includes(status);

    // для постов declinedReason запрещаем переход в статус MODERATION
    if (model.declinedReason === DeclinedReason.NEED_PRODUCT && status === PostStatus.MODERATION) {
        return false;
    }

    return hasPermission && isValidTransition;
};

export const canRemove = (model: PostModel, user?: UserModel): boolean =>
    canChangeStatus(model, PostStatus.DELETED, user) || Boolean(model.ownersData?.isOwner);

export async function updatePostStatus(model: PostModel, status: PostStatus) {
    const availableStatuses = STATUS_FINITE_STATE_MACHINE[model.status ?? PostStatus.PUBLISHED];

    if (!availableStatuses.includes(status)) {
        // TODO: Завести кастомный класс ошибок
        throw new Error('Недопустимый переход статуса');
    }

    await axios.patch(`/posts/${model.id}/status/${status}`);
}

const MEDIA_RATIO_MIN = 9 / 16;
const MEDIA_RATIO_MAX = 16 / 9;
const VIDEO_DURATION_MAX = 180;
const VIDEO_SIZE_MAX = 200 * 1024 * 1024;
const IMAGE_SIZE_MAX = 15 * 1024 * 1024;

export async function uploadPostMedia(file: File): Promise<MediaModel> {
    const fileExtension = getFileExtension(file);

    if (file.type.startsWith('video')) {
        const meta = await mediaService.getVideoFileMeta(file);
        const ratio = meta.width / meta.height;

        if (meta.size > VIDEO_SIZE_MAX) {
            throw new PostMediaUploadError('Видео должно быть менее 200Мб');
        }

        if (meta.duration > VIDEO_DURATION_MAX) {
            throw new PostMediaUploadError('Видео должно быть менее 3 минут');
        }

        if (ratio > MEDIA_RATIO_MAX || ratio < MEDIA_RATIO_MIN) {
            throw new PostMediaUploadError('Недопустимое соотношение сторон видео');
        }

        const blob = await mediaService.convertToBlob(file);
        const media = await mediaService.uploadBlob({
            blob,
            fileExtension,
            fileType: 'video',
            mediaSizes: {
                width: meta.width,
                height: meta.height,
            },
        });

        return {
            ...media,
            type: 'video',
            width: meta.width,
            height: meta.height,
        };
    }

    if (file.type.startsWith('image')) {
        const meta = await mediaService.getImageFileMeta(file);
        const ratio = meta.width / meta.height;

        if (meta.size > IMAGE_SIZE_MAX) {
            throw new PostMediaUploadError('Изображение должно быть менее 15Мб');
        }

        if (ratio > MEDIA_RATIO_MAX || ratio < MEDIA_RATIO_MIN) {
            throw new PostMediaUploadError('Недопустимое соотношение сторон изображения');
        }

        const blob = await mediaService.convertToBlob(file);
        const media = await mediaService.uploadBlob({
            blob,
            fileExtension,
            fileType: 'image',
            mediaSizes: {
                width: meta.width,
                height: meta.height,
            },
        });

        return { ...media, type: 'image', width: meta.width, height: meta.height };
    }

    throw new PostMediaUploadError('Неподдерживаемый тип файла');
}

export async function loadPostStatistic(postId: string) {
    const response = await axios.get(`/posts/${postId}/stat`);

    return {
        stats: toPostStatisticModel(response.data.stats),
    };
}

export async function searchPostByUrl(url: string) {
    const response = await axios.post('/posts/parse_product', { url });

    return toPostModel(response.data.post);
}

interface SavePostAsProductPost {
    post_id: string;
    collection_id: string;
    media: {
        id: string;
        type: string;
    }[];
}

export async function savePostAsProduct(post: SavePostAsProductPost): Promise<{
    collections: {
        collections: Array<CollectionsDataModel['id']>;
        records: {
            collections: CollectionsDataModel[];
        };
    };
    post: {
        records: {
            posts: PostDataModel[];
            business: BusinessModel[];
            categories: CategoryDataModel[];
        };
    };
}> {
    const response = await axios.post('/posts/save_product', post);

    const normalizedPost = normalize(
        { post: toPostModel(response.data.post) },
        { post: postSchema },
    );
    const collections = response.data.collections || [];
    const normalizedCollections = normalize(
        {
            collections: collections.map(toCollectionsModel),
        },
        {
            collections: [collectionsSchema],
        },
    );

    return {
        collections: {
            collections: normalizedCollections.result.collections,
            records: {
                collections: Object.values<CollectionsDataModel>(
                    normalizedCollections.entities.collections ?? {},
                ),
            },
        },
        post: {
            records: {
                posts: Object.values(normalizedPost.entities.posts ?? {}),
                business: Object.values(normalizedPost.entities.business ?? {}),
                categories: Object.values(normalizedPost.entities.categories ?? {}),
            },
        },
    };
}

export async function loadDiscovery(filters: {
    amount: number;
    no_log?: string | null;
    next?: string;
}) {
    const response = await axios.get('/discovery', { params: filters });

    const normalized = normalize(
        {
            ...response.data,
            posts: response.data.posts.map(toPostModel),
        },
        {
            posts: [postSchema],
        },
    );

    return {
        next: normalized.result.next,
        posts: normalized.result.posts,
        records: {
            posts: Object.values(normalized.entities.posts ?? {}),
            business: Object.values(normalized.entities.business ?? {}),
            categories: Object.values(normalized.entities.categories ?? {}),
        },
    };
}

export async function getPostSuggests(id: string, screen: EventSource): Promise<string[]> {
    const response = await axios.get(`/posts/${id}/suggest`);
    contentActionsService.trackPostSuggestsGenerate({ id, screen });

    return response?.data?.display_name_suggest;
}

export async function getPostsSuggests(ids: string[]): Promise<string[]> {
    const response = await axios.post('/posts/suggest', {
        post_ids: ids,
    });

    return response?.data?.display_name_suggest;
}

/**
 * Изменяет состояние активности продвижения поста.
 * @param postId - ID поста
 * @param enabled - Новое состояние продвижения (true/false)
 */
export async function updatePostBoosting(postId: string, _enabled: boolean): Promise<void> {
    return axios.post(`/posts/${postId}/boost`, {
        enabled: _enabled,
    });
}
